import * as React from "react";
import { useEffect, useRef } from "react";
import { useCookies } from "react-cookie";
import {
  Box,
  Button,
  Container,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Grid,
  ListItemButton,
} from "@mui/material";
import { MailOutline, PhoneIphone } from "@mui/icons-material";
import NearMeIcon from "@mui/icons-material/NearMe";

/* ── Bottom CTA ──────────────────────────────────────────── */
function BottomCTA() {
  const canvasRef = useRef(null);
  const [cookies] = useCookies(["user"]);
  const signupHref = cookies.user && cookies.user !== "undefined"
    ? "/filter/influencers"
    : "/authenticate/signup";
  const loginHref = cookies.user && cookies.user !== "undefined"
    ? "/filter/influencers"
    : "/authenticate/signin";

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    let rafId, lastTs = 0, visible = false;
    const FPS_CAP = 1000 / 30;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize, { passive: true });

    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible && !rafId) rafId = requestAnimationFrame(animate);
    }, { threshold: 0 });
    io.observe(canvas);

    const particles = Array.from({ length: 50 }, () => ({
      x:      Math.random() * canvas.width,
      y:      Math.random() * canvas.height,
      radius: Math.random() * 1.5 + 0.5,
      speedY: -(Math.random() * 0.4 + 0.2),
      speedX: (Math.random() - 0.5) * 0.2,
      coral:  Math.random() > 0.5,
    }));

    const animate = (ts) => {
      if (!visible) { rafId = null; return; }
      rafId = requestAnimationFrame(animate);
      if (ts - lastTs < FPS_CAP) return;
      lastTs = ts;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.speedX; p.y += p.speedY;
        if (p.y < -p.radius) { p.y = canvas.height + p.radius; p.x = Math.random() * canvas.width; }
        if (p.x < 0)           p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.coral ? "rgba(255,107,53,0.3)" : "rgba(255,255,255,0.1)";
        ctx.fill();
      });
    };
    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      io.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <Box
      className="reveal"
      sx={{
        position: "relative",
        bgcolor: "#0D1B3E",
        py: { xs: "60px", md: "96px" },
        px: { xs: "20px", md: "80px" },
        textAlign: "center",
        overflow: "hidden",
      }}
    >
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          zIndex: 0,
        }}
      />

      {/* Coral glow blob */}
      <Box
        sx={{
          position: "absolute",
          width: 500,
          height: 300,
          background: "radial-gradient(ellipse, rgba(255,107,53,0.15), transparent 70%)",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        {/* Label */}
        <Box
          component="span"
          sx={{
            display: "inline-block",
            color: "#FF6B35",
            bgcolor: "rgba(255,107,53,0.12)",
            border: "1px solid rgba(255,107,53,0.3)",
            px: "14px",
            py: "5px",
            borderRadius: "100px",
            fontSize: "10px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            mb: 3,
          }}
        >
          Get started today
        </Box>

        <Typography
          className="reveal reveal-delay-1"
          component="h2"
          sx={{
            fontSize: "clamp(28px, 3.5vw, 44px)",
            fontWeight: 800,
            color: "white",
            mb: 1.5,
          }}
        >
          Ready to find your next creator partner?
        </Typography>

        <Typography
          className="reveal reveal-delay-2"
          sx={{
            fontSize: "17px",
            color: "rgba(255,255,255,0.55)",
            mb: 4,
            maxWidth: "600px",
            mx: "auto",
          }}
        >
          Join hundreds of brands and agencies running smarter campaigns with Meesha.
        </Typography>

        <Box className="reveal reveal-delay-3">
          <Button
            href={signupHref}
            sx={{
              bgcolor: "#FF6B35",
              color: "white",
              fontWeight: 700,
              fontSize: "16px",
              px: "36px",
              py: "16px",
              borderRadius: "12px",
              textTransform: "none",
              display: "inline-flex",
              "&:hover": {
                bgcolor: "#e85520",
                transform: "translateY(-3px)",
                boxShadow: "0 12px 40px rgba(255,107,53,0.45)",
              },
              transition: "all 0.25s ease",
            }}
          >
            Start for free — no credit card needed
          </Button>

          <Typography sx={{ mt: 2, fontSize: "13px", color: "rgba(255,255,255,0.4)" }}>
            Already have an account?{" "}
            <Box
              component="a"
              href={loginHref}
              sx={{ color: "rgba(255,255,255,0.4)", textDecoration: "underline", "&:hover": { color: "white" } }}
            >
              Log in →
            </Box>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

/* ── Footer ──────────────────────────────────────────────── */
function FooterSection() {
  const [cookies] = useCookies(["user"]);
  const signupHref = cookies.user && cookies.user !== "undefined"
    ? "/filter/influencers"
    : "/authenticate/signup";
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "#0a1630",
        py: 5,
        px: { xs: 2, md: 5 },
        color: "white",
      }}
    >
      <Container maxWidth="xl">
        <Grid container rowSpacing={{ xs: 3, md: 0 }}>
          {/* Left: brand + contact */}
          <Grid item xs={6} md={4} paddingLeft={{ md: "4rem", xs: "0" }}>
            <Stack>
              <Typography
                variant="h6"
                fontWeight={800}
                component="a"
                href="/"
                sx={{ textDecoration: "none", color: "white", mb: 1 }}
              >
                MEESHA
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "13px", mb: 0.5 }}>
                Creatify Media Pvt. Ltd
              </Typography>

              <Typography
                display="flex"
                gap={0.5}
                sx={{ color: "rgba(255,255,255,0.55)", fontSize: "13px", mb: 0.5 }}
              >
                <span style={{ display: "flex" }}><PhoneIphone sx={{ fontSize: 16, mt: "2px" }} /></span>
                <span>+91 8142051271</span>
              </Typography>

              <Typography
                display="flex"
                gap={0.5}
                sx={{ color: "rgba(255,255,255,0.55)", fontSize: "13px", mb: 0.5 }}
              >
                <span style={{ display: "flex" }}><MailOutline sx={{ fontSize: 16, mt: "2px" }} /></span>
                <span>support@meesha.co.in</span>
              </Typography>

              <Typography
                display="flex"
                gap={0.5}
                sx={{ color: "rgba(255,255,255,0.55)", fontSize: "13px" }}
              >
                <span style={{ display: "flex" }}><NearMeIcon sx={{ fontSize: 16, mt: "2px" }} /></span>
                <span>
                  3rd Floor, Innov8<br />
                  Orchid Center, Sector 53<br />
                  Gurugram, Haryana 122022
                </span>
              </Typography>
            </Stack>
          </Grid>

          {/* Center: brand CTA */}
          <Grid item xs={12} md={4} order={{ xs: -1, md: 0 }}>
            <Stack spacing={2} alignItems="center" sx={{ textAlign: "center" }}>
              <Typography sx={{
                color:      "white",
                fontWeight: 800,
                fontSize:   "20px",
                lineHeight: 1.3,
              }}>
                India's #1 Creator<br />Intelligence Platform
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>
                2M+ creators · Verified contacts · Campaign reports
              </Typography>
              <Button
                href={signupHref}
                sx={{
                  background:    "linear-gradient(135deg, #FF6B35 0%, #FF4500 100%)",
                  color:         "white",
                  fontWeight:    700,
                  fontSize:      "14px",
                  px:            "28px",
                  py:            "12px",
                  borderRadius:  "10px",
                  textTransform: "none",
                  boxShadow:     "0 4px 20px rgba(255,107,53,0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #e85520 0%, #cc3300 100%)",
                    boxShadow:  "0 8px 30px rgba(255,107,53,0.45)",
                  },
                  transition:    "all 0.25s ease",
                }}
              >
                Create free account →
              </Button>
              <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.3)" }}>
                No credit card required
              </Typography>
            </Stack>
          </Grid>

          {/* Right: other links */}
          <Grid
            container
            item
            xs={6}
            md={4}
            paddingLeft={{ md: "4rem", xs: "0" }}
            justifyContent={{ md: "start", xs: "end" }}
          >
            <Stack>
              <ListItem disablePadding disableGutters>
                <ListItemText
                  primary="Other Links"
                  primaryTypographyProps={{ variant: "h6", sx: { color: "white", fontWeight: 700 } }}
                />
              </ListItem>

              {[
                { label: "Privacy Policy",                href: "/privacy_policy"   },
                { label: "Terms and Conditions",          href: "/terms_of_use"     },
                { label: "About us",                      href: "/about_us"         },
                { label: "Contact Us",                    href: "/policy/contactus" },
                { label: "Cancellation and Refund Policy",href: "/policy/refund"    },
                { label: "Shipping and Delivery Policy",  href: "/policy/shipping"  },
              ].map((link, i) => (
                <ListItem key={i} disablePadding disableGutters>
                  <ListItemButton sx={{ padding: 0 }} href={link.href}>
                    <ListItemText
                      primary={link.label}
                      primaryTypographyProps={{ sx: { color: "rgba(255,255,255,0.55)", fontSize: "13px" } }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function SubscribeThroughEmail(props) {
  return (
    <>
      <BottomCTA />
      <FooterSection />
    </>
  );
}

export default SubscribeThroughEmail;
